/**
 * Composants de rendu/édition par type de bloc. Chaque éditeur expose un
 * formulaire minimaliste sur les champs principaux du bloc et délègue la
 * persistance via onPatchData / onPatchParent.
 *
 * Pas de fancy stuff (Tiptap, etc.) — on reste sur des <textarea> simples,
 * style Notion-light. L'utilisateur peut split/merge/insérer/supprimer via la
 * toolbar du BlockShell.
 */

import { useRef, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import type { BlockComponentProps } from './types';
import { ClueHuntBlockEditor } from './clue-hunt/ClueHuntBlockEditor';
import {
  BufferedTextArea as TextArea,
  BufferedTextField as TextField,
  CommitButtons,
  useBufferedValue,
} from './buffered-fields';

function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-slate-600">{label}</label>
      {children}
      {hint ? <p className="text-[11px] text-slate-500">{hint}</p> : null}
    </div>
  );
}

/**
 * Textarea du champ markdown PRINCIPAL d'un bloc. Bufferisé (cf.
 * `useBufferedValue`) : rien n'est persisté tant que l'utilisateur n'a pas
 * validé (✓ / ⌘+Entrée / blur) ou annulé (✗ / Échap).
 *
 * Suit la sélection courante et affiche un bouton « Découper la sélection » dès
 * qu'une plage non vide (après trim) est sélectionnée — UNIQUEMENT quand le
 * champ est « propre » (pas de modification en attente), car le découpage opère
 * sur les offsets de la valeur PERSISTÉE. Le découpage crée jusqu'à 3 blocs
 * (avant / sélection / après), en sautant les parts vides.
 */
function PrimaryTextArea({
  value,
  onChange,
  disabled,
  rows = 4,
  onSplitRange,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  rows?: number;
  onSplitRange?: (start: number, end: number) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [sel, setSel] = useState<{ start: number; end: number } | null>(null);
  const { draft, setDraft, dirty, commit, reset } = useBufferedValue(value, onChange);

  const refreshSelection = () => {
    const el = ref.current;
    if (!el) return;
    const s = el.selectionStart ?? 0;
    const e = el.selectionEnd ?? 0;
    setSel(s !== e ? { start: Math.min(s, e), end: Math.max(s, e) } : null);
  };

  const selectedText = sel ? draft.slice(sel.start, sel.end) : '';
  // Découpe interdite tant qu'il reste des modifications non validées : les
  // offsets ne correspondraient pas au texte persité côté serveur.
  const canSplit = !dirty && !!(sel && onSplitRange && selectedText.trim().length > 0);

  return (
    <div className="space-y-1.5">
      <textarea
        ref={ref}
        className={cn(
          'w-full rounded-md border bg-white px-2 py-1.5 font-mono text-sm',
          'focus:outline-none focus:ring-1 focus:ring-slate-500',
          dirty ? 'border-emerald-400 ring-1 ring-emerald-200' : 'border-slate-300',
          disabled && 'opacity-60',
        )}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          requestAnimationFrame(refreshSelection);
        }}
        onSelect={refreshSelection}
        onKeyUp={refreshSelection}
        onMouseUp={refreshSelection}
        onBlur={commit}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            reset();
          }
        }}
        rows={rows}
        disabled={disabled}
      />
      {dirty ? (
        <div className="flex items-center gap-2">
          <CommitButtons onCommit={commit} onCancel={reset} disabled={disabled} />
          <span className="text-[11px] text-slate-400">
            ⌘/Ctrl + Entrée pour valider · Échap pour annuler
          </span>
        </div>
      ) : canSplit ? (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()} // évite le blur du textarea
          onClick={() => {
            if (!sel || !onSplitRange) return;
            onSplitRange(sel.start, sel.end);
            setSel(null);
          }}
          disabled={disabled}
          className="inline-flex min-h-9 items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
        >
          ✂ Découper la sélection en blocs ({sel!.end - sel!.start} car.)
        </button>
      ) : (
        <p className="text-[11px] text-slate-400">
          Astuce : sélectionnez un passage pour le découper en un nouveau bloc.
        </p>
      )}
    </div>
  );
}

function Select({
  value,
  onChange,
  disabled,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  options: ReadonlyArray<{ value: string; label: string }>;
}) {
  return (
    <select
      className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function StatementEditor({
  block,
  isDisabled,
  onPatchData,
  onSplitRange,
}: BlockComponentProps) {
  const data = block.data as { content_md?: string; title?: string };
  return (
    <div className="space-y-3">
      <Field label="Titre (optionnel)">
        <TextField
          value={data.title ?? ''}
          onChange={(v) => onPatchData({ ...data, title: v || undefined })}
          disabled={isDisabled}
        />
      </Field>
      <Field
        label="Énoncé (markdown)"
        hint="Contexte global, mise en situation. Affiché en haut de l'exercice."
      >
        <PrimaryTextArea
          value={data.content_md ?? ''}
          onChange={(v) => onPatchData({ ...data, content_md: v })}
          disabled={isDisabled}
          rows={6}
          onSplitRange={onSplitRange}
        />
      </Field>
    </div>
  );
}

export function StatementQuestionEditor({
  block,
  isDisabled,
  onPatchData,
  onSplitRange,
}: BlockComponentProps) {
  const data = block.data as {
    prompt_md?: string;
    sub_statement_md?: string;
    part_level?: number;
    difficulty?: string;
  };
  return (
    <div className="space-y-3">
      <Field label="Question posée à l'élève">
        <PrimaryTextArea
          value={data.prompt_md ?? ''}
          onChange={(v) => onPatchData({ ...data, prompt_md: v })}
          disabled={isDisabled}
          rows={3}
          onSplitRange={onSplitRange}
        />
      </Field>
      <Field
        label="Sous-énoncé (optionnel)"
        hint="Affiché uniquement à cette question. Ex : « a) Donner v(t) »."
      >
        <TextArea
          value={data.sub_statement_md ?? ''}
          onChange={(v) => onPatchData({ ...data, sub_statement_md: v || undefined })}
          disabled={isDisabled}
          rows={2}
        />
      </Field>
      <div className="flex gap-3">
        <Field label="Niveau hiérarchique">
          <Select
            value={String(data.part_level ?? '')}
            onChange={(v) => onPatchData({ ...data, part_level: v ? Number(v) : undefined })}
            disabled={isDisabled}
            options={[
              { value: '', label: '— continuation —' },
              { value: '1', label: '1 (Partie I)' },
              { value: '2', label: '2 (a, b, c)' },
              { value: '3', label: '3 (i, ii, iii)' },
            ]}
          />
        </Field>
        <Field label="Difficulté">
          <Select
            value={data.difficulty ?? ''}
            onChange={(v) => onPatchData({ ...data, difficulty: v || undefined })}
            disabled={isDisabled}
            options={[
              { value: '', label: '— hérite —' },
              { value: 'easy', label: 'Facile' },
              { value: 'medium', label: 'Moyen' },
              { value: 'hard', label: 'Difficile' },
            ]}
          />
        </Field>
      </div>
    </div>
  );
}

export function CorrectionEditor({
  block,
  questions,
  isDisabled,
  onPatchData,
  onPatchParent,
  onSplitRange,
}: BlockComponentProps) {
  const data = block.data as {
    explanation_md?: string;
    prompt_md?: string;
    answer_md?: string;
    answer_mode?: string;
    equation_before_md?: string;
    equation_after_md?: string;
    transformation_label_md?: string;
  };
  return (
    <div className="space-y-3">
      <Field
        label="Type de bloc corrigé"
        hint={
          block.parent_id
            ? "Point de contrôle QCM — la bonne réponse est demandée à l'élève."
            : 'Étape intermédiaire — pas de QCM associé.'
        }
      >
        <Select
          value={block.parent_id ?? ''}
          onChange={(v) => onPatchParent(v || null)}
          disabled={isDisabled}
          options={[
            { value: '', label: '— étape intermédiaire (aucune question) —' },
            ...questions.map((q) => ({
              value: q.id,
              label: `QCM pour : ${(q.data as { prompt_md?: string }).prompt_md?.slice(0, 60) ?? '(sans titre)'}`,
            })),
          ]}
        />
      </Field>
      <Field label="Explication (markdown)">
        <PrimaryTextArea
          value={data.explanation_md ?? ''}
          onChange={(v) => onPatchData({ ...data, explanation_md: v })}
          disabled={isDisabled}
          rows={4}
          onSplitRange={onSplitRange}
        />
      </Field>
      {block.parent_id ? (
        <Field
          label="Consigne de l'étape (prompt_md, optionnel)"
          hint="Phrase courte affichée AVANT les choix (ex. « Les coefficients multiplicateurs sont : »). Donne le contexte du sous-QCM — indispensable quand une question a plusieurs étapes."
        >
          <TextField
            value={data.prompt_md ?? ''}
            onChange={(v) => onPatchData({ ...data, prompt_md: v || undefined })}
            disabled={isDisabled}
          />
        </Field>
      ) : null}
      {block.parent_id ? (
        <Field
          label="Bonne réponse (answer_md)"
          hint="Présentée comme l'option correcte du QCM. Les distracteurs rattachés à ce bloc seront les options incorrectes."
        >
          <TextField
            value={data.answer_md ?? ''}
            onChange={(v) => onPatchData({ ...data, answer_md: v })}
            disabled={isDisabled}
            mono
          />
        </Field>
      ) : null}
      {block.parent_id ? (
        <Field
          label="Mode de réponse"
          hint="« Plusieurs réponses » : l'élève coche toutes les bonnes réponses puis valide (scoring tout-ou-rien). Marquez alors ≥ 1 distracteur comme « aussi correct »."
        >
          <Select
            value={data.answer_mode === 'multiple' ? 'multiple' : 'single'}
            onChange={(v) =>
              onPatchData({ ...data, answer_mode: v === 'multiple' ? 'multiple' : undefined })
            }
            disabled={isDisabled}
            options={[
              { value: 'single', label: 'Réponse unique' },
              { value: 'multiple', label: 'Plusieurs réponses' },
            ]}
          />
        </Field>
      ) : null}
      <details className="text-xs">
        <summary className="cursor-pointer text-slate-600">
          Champs équation step-by-step (avancé)
        </summary>
        <div className="mt-2 space-y-2">
          <Field label="Équation avant">
            <TextArea
              value={data.equation_before_md ?? ''}
              onChange={(v) => onPatchData({ ...data, equation_before_md: v || undefined })}
              disabled={isDisabled}
              rows={1}
            />
          </Field>
          <Field label="Équation après">
            <TextArea
              value={data.equation_after_md ?? ''}
              onChange={(v) => onPatchData({ ...data, equation_after_md: v || undefined })}
              disabled={isDisabled}
              rows={1}
            />
          </Field>
          <Field label="Label transformation">
            <TextField
              value={data.transformation_label_md ?? ''}
              onChange={(v) => onPatchData({ ...data, transformation_label_md: v || undefined })}
              disabled={isDisabled}
            />
          </Field>
        </div>
      </details>
    </div>
  );
}

export function DistractorEditor({
  block,
  corrections,
  isDisabled,
  onPatchData,
  onPatchParent,
}: BlockComponentProps) {
  const data = block.data as {
    label_md?: string;
    error_type?: string;
    feedback_md?: string;
    is_correct?: boolean;
  };
  const isCorrect = data.is_correct === true;
  return (
    <div className="space-y-3">
      <Field
        label="Rattaché à la correction"
        hint="Cette proposition sera affichée comme option du QCM de cette correction."
      >
        <Select
          value={block.parent_id ?? ''}
          onChange={(v) => onPatchParent(v || null)}
          disabled={isDisabled}
          options={[
            { value: '', label: '— (aucune) —' },
            ...corrections
              .filter((c) => c.parent_id !== null) // seules les corrections QCM ont du sens
              .map((c) => ({
                value: c.id,
                label: `${(c.data as { answer_md?: string }).answer_md?.slice(0, 40) ?? '(sans bonne réponse)'}`,
              })),
          ]}
        />
      </Field>
      {/* QCM à réponses multiples : ce « distracteur » est en fait une bonne
          réponse supplémentaire. Un choix correct ne porte pas de type d'erreur. */}
      <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={isCorrect}
          disabled={isDisabled}
          onChange={(e) =>
            onPatchData({
              ...data,
              is_correct: e.target.checked ? true : undefined,
              error_type: e.target.checked ? undefined : data.error_type,
            })
          }
        />
        Cette proposition est aussi correcte (QCM à réponses multiples)
      </label>
      <Field label={isCorrect ? 'Libellé de la bonne réponse' : 'Libellé de la mauvaise réponse'}>
        <TextArea
          value={data.label_md ?? ''}
          onChange={(v) => onPatchData({ ...data, label_md: v })}
          disabled={isDisabled}
          rows={1}
        />
      </Field>
      {!isCorrect ? (
        <div className="flex gap-3">
          <Field label="Type d'erreur">
            <Select
              value={data.error_type ?? ''}
              onChange={(v) => onPatchData({ ...data, error_type: v || undefined })}
              disabled={isDisabled}
              options={[
                { value: '', label: '— aucun —' },
                { value: 'calcul', label: 'Calcul' },
                { value: 'raisonnement', label: 'Raisonnement' },
                { value: 'lecture', label: 'Lecture' },
                { value: 'signe', label: 'Signe' },
              ]}
            />
          </Field>
        </div>
      ) : null}
      <Field label="Feedback affiché si l'élève choisit cette réponse">
        <TextArea
          value={data.feedback_md ?? ''}
          onChange={(v) => onPatchData({ ...data, feedback_md: v || undefined })}
          disabled={isDisabled}
          rows={2}
        />
      </Field>
    </div>
  );
}

export function HintEditor({
  block,
  questions,
  isDisabled,
  onPatchData,
  onPatchParent,
  onSplitRange,
}: BlockComponentProps) {
  const data = block.data as { body_md?: string; xp_malus?: number };
  return (
    <div className="space-y-3">
      <Field label="Rattaché à la question">
        <Select
          value={block.parent_id ?? ''}
          onChange={(v) => onPatchParent(v || null)}
          disabled={isDisabled}
          options={questions.map((q) => ({
            value: q.id,
            label: (q.data as { prompt_md?: string }).prompt_md?.slice(0, 60) ?? '(sans titre)',
          }))}
        />
      </Field>
      <Field label="Texte de l'indice" hint="Affiché si l'élève demande de l'aide (malus XP).">
        <PrimaryTextArea
          value={data.body_md ?? ''}
          onChange={(v) => onPatchData({ ...data, body_md: v })}
          disabled={isDisabled}
          rows={3}
          onSplitRange={onSplitRange}
        />
      </Field>
      <Field label="Malus XP si utilisé">
        <input
          type="number"
          min={0}
          max={100}
          className="w-24 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
          value={data.xp_malus ?? 0}
          onChange={(e) => onPatchData({ ...data, xp_malus: Number(e.target.value) || 0 })}
          disabled={isDisabled}
        />
      </Field>
    </div>
  );
}

export function ExplanationEditor({
  block,
  isDisabled,
  onPatchData,
  onSplitRange,
}: BlockComponentProps) {
  const data = block.data as { body_md?: string; scope?: string };
  return (
    <div className="space-y-3">
      <Field label="Quand afficher cette explication">
        <Select
          value={data.scope ?? 'always'}
          onChange={(v) => onPatchData({ ...data, scope: v })}
          disabled={isDisabled}
          options={[
            { value: 'always', label: 'Toujours' },
            { value: 'on_correct', label: 'Si réponse correcte' },
            { value: 'on_wrong', label: 'Si réponse incorrecte' },
          ]}
        />
      </Field>
      <Field label="Texte (markdown)">
        <PrimaryTextArea
          value={data.body_md ?? ''}
          onChange={(v) => onPatchData({ ...data, body_md: v })}
          disabled={isDisabled}
          rows={4}
          onSplitRange={onSplitRange}
        />
      </Field>
    </div>
  );
}

export function NoteEditor({ block, isDisabled, onPatchData, onSplitRange }: BlockComponentProps) {
  const data = block.data as { body_md?: string };
  return (
    <Field label="Note tuteur (privée, non visible élève)">
      <PrimaryTextArea
        value={data.body_md ?? ''}
        onChange={(v) => onPatchData({ ...data, body_md: v })}
        disabled={isDisabled}
        rows={3}
        onSplitRange={onSplitRange}
      />
    </Field>
  );
}

export function renderBlockEditor(props: BlockComponentProps) {
  switch (props.block.block_type) {
    case 'statement':
      return <StatementEditor {...props} />;
    case 'statement_question':
      return <StatementQuestionEditor {...props} />;
    case 'correction':
      return <CorrectionEditor {...props} />;
    case 'distractor':
      return <DistractorEditor {...props} />;
    case 'hint':
      return <HintEditor {...props} />;
    case 'explanation':
      return <ExplanationEditor {...props} />;
    case 'note':
      return <NoteEditor {...props} />;
    case 'clue_hunt':
      return <ClueHuntBlockEditor {...props} />;
  }
}
